import { createContext, useCallback, useReducer } from 'react';
import globalReducer, { TGlobalContent } from './globalReducer';
import actionTypes from './actions';

const initialState: TGlobalContent = {
  screen: null,
  currentRetrospectiveId: undefined,
  retrospective: undefined,
  teamMood: [],
  cards: [],
  user: undefined,
  isLoading: false,
  onLoading() {
    throw new Error('onLoading not implemented');
  },
  onChangeScreen() {
    throw new Error('onChangeScreen not implemented');
  },
};

export const GlobalContext = createContext<TGlobalContent>(initialState);

const {
  ON_LOADING,
  CHANGE_SCREEN,
  LOAD_RETROSPECTIVE,
  LOAD_USER,
  UPDATE_MOODS,
  UPDATE_CARDS,
  REORDER_CARDS,
  MOVE_AND_UPDATE_CARDS,
  COMBINE_CARDS,
} = actionTypes;

export function useGlobalStateValue() {
  const [currentState, dispatch] = useReducer(globalReducer, initialState);

  const onLoading = useCallback((loading) => {
    dispatch({
      type: ON_LOADING,
      payload: loading,
    });
  }, []);

  const onChangeScreen = useCallback((screen, retrospectiveId) => {
    dispatch({
      type: CHANGE_SCREEN,
      payload: { screen, retrospectiveId },
    });
  }, []);

  const onLoadRetrospective = useCallback((retrospective) => {
    dispatch({
      type: LOAD_RETROSPECTIVE,
      payload: retrospective,
    });
  }, []);

  const onLoadUser = useCallback((user) => {
    dispatch({
      type: LOAD_USER,
      payload: user,
    });
  }, []);

  const onUpdateCards = useCallback((cards) => {
    dispatch({
      type: UPDATE_CARDS,
      payload: cards,
    });
  }, []);

  const onUpdateMoods = useCallback((moods) => {
    dispatch({
      type: UPDATE_MOODS,
      payload: moods,
    });
  }, []);

  const onReorderCards = useCallback((source, destination) => {
    dispatch({
      type: REORDER_CARDS,
      payload: { source, destination },
    });
  }, []);

  const onMoveCards = useCallback((source, destination) => {
    dispatch({
      type: MOVE_AND_UPDATE_CARDS,
      payload: { source, destination },
    });
  }, []);

  const onCombineCards = useCallback(
    (cardIdToUpdate, cardIdToDelete, description) => {
      dispatch({
        type: COMBINE_CARDS,
        payload: { cardIdToUpdate, cardIdToDelete, description },
      });
    },
    []
  );

  return {
    ...currentState,
    onLoading,
    onChangeScreen,
    onLoadRetrospective,
    onLoadUser,
    onUpdateCards,
    onUpdateMoods,
    onReorderCards,
    onMoveCards,
    onCombineCards,
  };
}
